import { useState } from 'react'
import { Button, buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '@cocoonspace/ui/components/carousel'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { Trans, useLingui } from '@lingui/react/macro'
import { TwicImg } from '@twicpics/components/react'
import Autoplay from 'embla-carousel-autoplay'
import { ArrowRightIcon } from 'lucide-react'
import Link from 'next/link'

const URLS_REFS = {
	cocoon: '/paris',
	cocoons:
		'https://cocoonspace.typeform.com/to/xfYeqyLX#offer=multiple-cocoons',
	custom: 'https://cocoonspace.typeform.com/to/xfYeqyLX#offer=private-floor',
	desk: '/desks/paris',
} as const

type SpaceType = keyof typeof URLS_REFS

export const SpacePicker = () => {
	const { t } = useLingui()
	const [spaceType, setSpaceType] = useState<SpaceType>('cocoon')

	return (
		<Card className='flex w-full flex-col items-start gap-4 divide-y p-4 md:w-[400px]'>
			<div className='flex w-full items-center justify-between gap-4'>
				<div className='font-semibold whitespace-nowrap md:text-lg'>
					{t`Je recherche :`}
				</div>

				<Select
					onValueChange={(value) => setSpaceType(value as SpaceType)}
					value={spaceType}
				>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>

					<SelectContent>
						<SelectItem value='cocoon'>{t`Un Cocoon`}</SelectItem>
						<SelectItem value='cocoons'>{t`Plusieurs Cocoons`}</SelectItem>
						<SelectItem value='custom'>{t`Un étage privatisé`}</SelectItem>
						<SelectItem value='desk'>{t`Un Desk`}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<Link
				className={buttonVariants({ className: 'w-full' })}
				href={URLS_REFS[spaceType]}
				target={
					['cocoons', 'custom'].includes(spaceType) ? '_blank' : undefined
				}
			>
				{t`Trouver mon espace`} <ArrowRightIcon className='ml-1' />
			</Link>
		</Card>
	)
}

const HomeCarousel = () => (
	<Carousel
		className='w-full'
		opts={{ loop: true }}
		plugins={[Autoplay()]}
	>
		<CarouselContent className='m-0 size-full h-[70vh] md:h-screen'>
			{Array.from({ length: 3 }).map((_, index) => (
				<CarouselItem
					key={index}
					className='size-full p-0'
				>
					<TwicImg
						alt=''
						className='z-0 h-full w-full object-cover'
						focus='auto'
						ratio='none'
						src={`/content/home/carousel-${index + 1}.jpg`}
					/>
				</CarouselItem>
			))}
		</CarouselContent>

		<div className='to-primary/10 absolute inset-0 bg-gradient-to-b from-black/50 to-40% md:bg-gradient-to-r' />
	</Carousel>
)

const HomeIntroContent = () => {
	const { t } = useLingui()

	return (
		<Card className='absolute inset-x-0 top-16 z-10 flex scale-90 flex-col items-start justify-between gap-8 p-6 md:top-[20%] md:right-auto md:left-[7%] md:scale-100 md:justify-start md:p-8'>
			<div className='flex flex-col gap-2'>
				<div className='z-10 text-5xl leading-none font-bold md:text-[50px]'>
					cocoon space
				</div>

				<div className='text-lg font-semibold md:text-lg'>
					<Trans>
						Des lieux privés à l'heure, sans engagement,
						<br />
						accessibles en 3 clics.
					</Trans>
				</div>
			</div>

			<div className='flex w-full flex-col items-start gap-2'>
				<Button
					className='w-full'
					asChild
				>
					<Link href='/paris'>
						{t`Réserver un Cocoon maintenant`}{' '}
						<ArrowRightIcon className='ml-1' />
					</Link>
				</Button>
			</div>
		</Card>
	)
}

export const HomeIntro = () => (
	<div className='relative flex h-[70vh] w-full flex-col items-start md:h-screen md:justify-center'>
		<HomeCarousel />
		<HomeIntroContent />
	</div>
)
